/**
 * Atalhos de texto dos editores (notícia e análise): "/atalho" seguido de
 * espaço, vírgula, ponto ou Enter vira o conteúdo cadastrado em Atalhos de
 * Texto (com vírgula/ponto, a pontuação fica colada depois: "Soja Spot,").
 *
 * Os gatilhos chegam em momentos diferentes:
 *   - espaço, vírgula e ponto: no keyup, quando o caractere já está no
 *     texto ("/fonte ", "/fonte,");
 *   - Enter: no keydown, ANTES de o Jodit quebrar a linha — depois disso o
 *     "/fonte" já ficou no parágrafo de cima e o cursor no de baixo. Por
 *     isso o Enter que aplica o atalho é consumido: aplica e não pula linha
 *     (um segundo Enter pula, como de costume).
 */

/** Tenta aplicar o atalho que termina no cursor. Devolve true se aplicou. */
export function aplicarAtalho(jodit, atalhos, gatilho) {
  const sel = jodit?.s?.sel;
  if (!sel || !sel.rangeCount) return false;
  const range = sel.getRangeAt(0);
  if (!range.collapsed) return false;
  const node = range.startContainer;
  if (!node || node.nodeType !== Node.TEXT_NODE) return false;

  // O Jodit converte o espaço digitado em nbsp dentro do contentEditable
  // pra evitar colapso de whitespace do HTML — normaliza pra espaço comum
  // antes de comparar.
  const textBefore = node.textContent.slice(0, range.startOffset).replace(/ /g, " ");
  const PADROES = {
    enter: /(^|\s)\/([a-zA-Z0-9_-]+)$/,
    espaco: /(^|\s)\/([a-zA-Z0-9_-]+) $/,
    // "/soja," e "/soja." — a pontuação fica depois da expansão. Exige
    // espaço (ou início) antes da barra, então endereço como
    // "site.com/soja." não dispara.
    pontuacao: /(^|\s)\/([a-zA-Z0-9_-]+)([.,])$/,
  };
  const match = textBefore.match(PADROES[gatilho]);
  if (!match) return false;

  const expansao = atalhos[match[2].toLowerCase()];
  if (!expansao) return false;

  const matchLength = match[0].length - (match[1] ? match[1].length : 0);
  const startOffset = range.startOffset - matchLength;

  const deleteRange = document.createRange();
  deleteRange.setStart(node, startOffset);
  deleteRange.setEnd(node, range.startOffset);
  deleteRange.deleteContents();

  const newRange = document.createRange();
  newRange.setStart(node, startOffset);
  newRange.collapse(true);
  sel.removeAllRanges();
  sel.addRange(newRange);

  // O espaço que a pessoa digitou para disparar o atalho é consumido na
  // substituição, então o texto seguinte vinha colado na expansão. Um
  // espaço no fim devolve o que ela digitou (no Enter também, para a frase
  // poder continuar). Só não acrescenta quando o conteúdo já termina em
  // espaço, para não dobrar.
  // Na pontuação, nada de espaço: a vírgula/ponto vai colada no texto
  // ("Soja Spot,"), e o espaço de quem cadastrou o atalho com espaço no fim
  // sai, para não ficar "Soja Spot ,".
  const comEspaco =
    gatilho === "pontuacao"
      ? `${expansao.replace(/(\s|&nbsp;)+$/i, "")}${match[3]}`
      : /(\s|&nbsp;)$/i.test(expansao)
      ? expansao
      : `${expansao} `;

  // Mesma técnica de marcador inline já usada pro resto do editor: o
  // Jodit só respeita a posição do cursor pra conteúdo inline, então
  // insere um marcador ali e troca ele pelo HTML real via DOM direto.
  const markerId = `rb-shortcut-marker-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  jodit.s.insertHTML(`<span id="${markerId}">​</span>`);
  const marker = document.getElementById(markerId);
  if (marker) {
    // Trocar o marcador por outerHTML deixava o cursor DENTRO da última
    // tag da expansão: num atalho como "<strong>Negrito</strong>" tudo o
    // que fosse digitado em seguida saía em negrito e colado. Inserindo
    // como fragmento dá para reposicionar o cursor logo depois do último
    // nó — que é justamente o espaço acrescentado acima.
    const alcance = document.createRange();
    alcance.selectNode(marker);
    const fragmento = alcance.createContextualFragment(comEspaco);
    const ultimoNo = fragmento.lastChild;
    marker.replaceWith(fragmento);

    if (ultimoNo) {
      const depois = document.createRange();
      depois.setStartAfter(ultimoNo);
      depois.collapse(true);
      sel.removeAllRanges();
      sel.addRange(depois);
    }
  } else {
    jodit.s.insertHTML(comEspaco);
  }
  return true;
}

/**
 * Liga os gatilhos numa instância do Jodit. `obterAtalhos` devolve o
 * mapa atual; `aoAplicar` recebe o HTML novo do editor.
 *
 * O Enter é escutado na fase de captura no contêiner do editor (ancestral
 * da área editável): assim roda antes do keydown do próprio Jodit, que é
 * quem cria o parágrafo novo, e pode cancelá-lo.
 */
export function ligarAtalhos(instance, obterAtalhos, aoAplicar) {
  if (!instance?.editor) return;
  desligarAtalhos(instance);

  // Espaço, vírgula e ponto chegam no keyup, com o caractere já no texto.
  const noEspaco = (event) => {
    const gatilho = event.key === " " ? "espaco" : event.key === "," || event.key === "." ? "pontuacao" : null;
    if (!gatilho) return;
    if (aplicarAtalho(instance, obterAtalhos(), gatilho)) aoAplicar(instance.value);
  };
  const noEnter = (event) => {
    if (event.key !== "Enter" || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey || event.isComposing) return;
    if (!instance.editor.contains(event.target)) return;
    if (!aplicarAtalho(instance, obterAtalhos(), "enter")) return;
    event.preventDefault();
    event.stopPropagation();
    aoAplicar(instance.value);
  };

  const raiz = instance.container || instance.editor.parentElement || instance.editor;
  instance.editor.addEventListener("keyup", noEspaco);
  raiz.addEventListener("keydown", noEnter, true);
  instance.__rbAtalhos = () => {
    instance.editor.removeEventListener("keyup", noEspaco);
    raiz.removeEventListener("keydown", noEnter, true);
  };
}

export function desligarAtalhos(instance) {
  if (instance?.__rbAtalhos) {
    instance.__rbAtalhos();
    delete instance.__rbAtalhos;
  }
}
